/**
 * Display formatters. Centralised because a date rendered three different ways
 * on one screen looks broken, and because every one of these has a null case
 * that must not reach the user as "Invalid Date" or "NaN".
 *
 * Locale is an explicit parameter rather than an implicit read of the runtime
 * locale. Passing `undefined` means "use the browser's", which is the current
 * default; the tenant's `localization.locale` setting is meant to drive it, and
 * an argument makes that swap a one-line change instead of a hunt. It also
 * makes these functions testable — otherwise the output depends on whose
 * machine runs the test (en-IN renders 4,820,000 as "48.2L", not "4.82M").
 */

export type Locale = string | undefined;

/**
 * The currency every money figure without its own currency is shown in — the
 * tenant's Finance "Reporting currency" (`finance.baseCurrency`), pushed in by
 * useTenantCurrency() once the tenant is known. Read at call time, so the
 * ~130 call sites that pass no currency follow the setting untouched; a
 * per-record `{ currency }` (an invoice's, a deal's) still wins.
 */
export const FALLBACK_CURRENCY = 'USD';
let defaultCurrency = FALLBACK_CURRENCY;

/**
 * The platform's own SaaS subscription prices, MRR and revenue (Platform
 * Console) are billed in USD and belong to no tenant — they must not follow
 * whichever tenant the viewing admin happens to be signed into.
 */
export const PLATFORM_CURRENCY = 'USD';

export function setDefaultCurrency(code: string | null | undefined): void {
  defaultCurrency = /^[A-Z]{3}$/.test(code ?? '') ? (code as string) : FALLBACK_CURRENCY;
}

export function getDefaultCurrency(): string {
  return defaultCurrency;
}

/** The designs show dates as "Aug 15" / "Aug 15, 2026". */
export function formatDate(
  value: string | null | undefined,
  opts?: { year?: boolean; locale?: Locale },
): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(opts?.locale, {
    month: 'short',
    day: 'numeric',
    ...(opts?.year && { year: 'numeric' }),
  });
}

/** Compact money, as the KPI cards show ("$4.82M"). */
export function formatCompactMoney(
  value: number | null | undefined,
  { currency = defaultCurrency, locale }: { currency?: string; locale?: Locale } = {},
): string {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatMoney(
  value: number | null | undefined,
  { currency = defaultCurrency, locale }: { currency?: string; locale?: Locale } = {},
): string {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * "Due in 3 days" / "5 days overdue". Uses whole days from today so a task due
 * later today reads "today" rather than "in 4 hours", matching how people
 * actually talk about due dates.
 */
export function formatDueRelative(value: string | null | undefined, now = new Date()): string | null {
  if (!value) return null;
  const due = new Date(value);
  if (Number.isNaN(due.getTime())) return null;

  const startOfDay = (d: Date) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const days = Math.round((startOfDay(due) - startOfDay(now)) / 86400000);

  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days === -1) return '1 day overdue';
  if (days < 0) return `${Math.abs(days)} days overdue`;
  return `Due in ${days} days`;
}

/** Clamped percentage for progress bars — a width over 100% breaks the layout. */
export function clampPercent(value: number | null | undefined): number {
  if (value == null || Number.isNaN(value)) return 0;
  return Math.min(100, Math.max(0, Math.round(value)));
}

/** ISO date (YYYY-MM-DD) for date inputs, which reject a full timestamp. */
export function toDateInput(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/**
 * Today as "YYYY-MM-DD", in the browser's local timezone — NOT
 * `toISOString()` (UTC), which can read as "yesterday" or "tomorrow" near
 * local midnight depending on the viewer's offset. Used as a DatePicker
 * `minDate`/default wherever a date can't be backdated (Project/Task start
 * dates).
 */
export function todayDateOnly(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
